import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import TituloAdmin from '@components/TituloAdmin';
import Boton from '@components/Boton';
import {
  createEmpresaZonaApi,
  getAllEmpresasZonasApi,
} from "@api/wp/empresasZonas/empresasZonasApi";
import { getAllEmpresasApi } from "@api/wp/empresas/empresasApi";
import { getAllZonasApi } from "@api/wp/zonas/zonasApi";
import { colors } from '@styles/colors';
import { selectStyle } from '@styles/selecsEstylos';
import '@styles/select2Custom.css';

import { cajaPrincipalStyle } from '@styles/cajaPrincipal';
import { cajaContenedorAdminStyle } from '@styles/cajaContenedorAdmin';
import Label from '@components/Label';
import LoadingOverlay from '@components/LoadingOverlay';
import LabelCarga from '@components/LabelCarga';
import '@styles/modernTable.css';

function EmpresasZonas() {
  const navigate = useNavigate();
  // Estados para los datos
  const [empresas, setEmpresas] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [empresasZonas, setEmpresasZonas] = useState([]);

  // Estados para el control de la UI
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  // Estados para el formulario
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState('');
  const [zonaSeleccionada, setZonaSeleccionada] = useState('');

  const empresaSelectRef = useRef(null);
  const zonaSelectRef = useRef(null);

  // Validación de token: si no existe, redirigir a login
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    cargarDatos();
  }, []);

  // Select2 — mismo criterio que CitasExtras.jsx
  useEffect(() => {
    try {
      const $ = window.$;
      if (!empresaSelectRef.current || !$?.fn?.select2) {
        return undefined;
      }
      const $el = $(empresaSelectRef.current);
      if ($el.hasClass('select2-hidden-accessible')) {
        $el.off('change.select2react');
        $el.select2('destroy');
      }
      $el.select2({
        placeholder: '-- Seleccione una empresa --',
        allowClear: true,
        width: '100%',
        theme: 'default',
        minimumResultsForSearch: 0,
        language: {
          noResults: function () { return 'No se encontraron resultados'; },
          searching: function () { return 'Buscando...'; },
        },
      });
      $el.on('change.select2react', function () {
        setEmpresaSeleccionada(String($el.val() || ''));
      });
      $el.val(empresaSeleccionada || '').trigger('change');
      return () => {
        if ($el.hasClass('select2-hidden-accessible')) {
          $el.off('change.select2react');
          $el.select2('destroy');
        }
      };
    } catch (e) {
      console.error('Error inicializando Select2 (empresa):', e);
      return undefined;
    }
  }, [empresas]);

  useEffect(() => {
    try {
      const $ = window.$;
      if (!zonaSelectRef.current || !$?.fn?.select2) {
        return undefined;
      }
      const $el = $(zonaSelectRef.current);
      if ($el.hasClass('select2-hidden-accessible')) {
        $el.off('change.select2react');
        $el.select2('destroy');
      }
      $el.select2({
        placeholder: '-- Seleccione una zona --',
        allowClear: true,
        width: '100%',
        theme: 'default',
        minimumResultsForSearch: 0,
        language: {
          noResults: function () { return 'No se encontraron resultados'; },
          searching: function () { return 'Buscando...'; },
        },
      });
      $el.on('change.select2react', function () {
        setZonaSeleccionada(String($el.val() || ''));
      });
      $el.val(zonaSeleccionada || '').trigger('change');
      return () => {
        if ($el.hasClass('select2-hidden-accessible')) {
          $el.off('change.select2react');
          $el.select2('destroy');
        }
      };
    } catch (e) {
      console.error('Error inicializando Select2 (zona):', e);
      return undefined;
    }
  }, [zonas]);

  useEffect(() => {
    try {
      const $ = window.$;
      const el = empresaSelectRef.current;
      if (!el || !$?.fn?.select2) return;
      const $el = $(el);
      if (!$el.hasClass('select2-hidden-accessible')) return;
      const v = empresaSeleccionada || '';
      if ($el.val() !== v) {
        $el.val(v).trigger('change');
      }
    } catch (_) {
      /* noop */
    }
  }, [empresaSeleccionada]);

  useEffect(() => {
    try {
      const $ = window.$;
      const el = zonaSelectRef.current;
      if (!el || !$?.fn?.select2) return;
      const $el = $(el);
      if (!$el.hasClass('select2-hidden-accessible')) return;
      const v = zonaSeleccionada || '';
      if ($el.val() !== v) {
        $el.val(v).trigger('change');
      }
    } catch (_) {
      /* noop */
    }
  }, [zonaSeleccionada]);

  // Función para cargar todos los datos necesarios
  const cargarDatos = async () => {
    try {
      setCargando(true);
      setError('');
      setMensaje('');

      // Cargar empresas, zonas y relaciones existentes en paralelo
      const [empresasResponse, zonasResponse, relacionesResponse] = await Promise.all([
        getAllEmpresasApi(),
        getAllZonasApi(),
        getAllEmpresasZonasApi()
      ]);

      setEmpresas(empresasResponse.result || []);
      setZonas(zonasResponse.result || []);
      setEmpresasZonas(relacionesResponse.result || []);

      if (relacionesResponse.result && relacionesResponse.result.length > 0) {
        setMensaje(`Se cargaron ${relacionesResponse.result.length} relaciones empresa-zona`);
      }

    } catch (err) {
      setError('Error al cargar los datos: ' + err.message);
      console.error('Error:', err);
    } finally {
      setCargando(false);
    }
  };

  // Función para crear una nueva relación empresa-zona
  const crearRelacion = async () => {
    if (!empresaSeleccionada || !zonaSeleccionada) {
      setError('Por favor seleccione una empresa y una zona');
      return;
    }

    // Verificar si la relación ya existe
    const relacionExiste = empresasZonas.some(
      relacion => relacion.idEmp == empresaSeleccionada && relacion.idZona == zonaSeleccionada
    );

    if (relacionExiste) {
      setError('Esta relación empresa-zona ya existe');
      return;
    }

    try {
      setProcesando(true);
      setError('');
      setMensaje('');

      const data = {
        idEmp: parseInt(empresaSeleccionada),
        idZona: parseInt(zonaSeleccionada)
      };

      await createEmpresaZonaApi(data);
      
      // Limpiar el formulario
      setEmpresaSeleccionada('');
      setZonaSeleccionada('');
      
      // Recargar los datos
      await cargarDatos();
      
      setMensaje('Relación empresa-zona creada exitosamente');

    } catch (err) {
      setError('Error al crear la relación: ' + err.message);
      console.error('Error:', err);
    } finally {
      setProcesando(false);
    }
  };

  const alertStyle = (type) => ({
    padding: '1.5vh 2vh',
    marginBottom: '2vh',
    borderRadius: '0.8vh',
    fontSize: '1.4vh',
    fontWeight: '500',
    fontFamily: 'Inter',
    border: `1px solid ${type === 'error' ? colors.errorBorder : colors.successBorder}`,
    backgroundColor: type === 'error' ? colors.errorBg2 : colors.infoBg2,
    color: type === 'error' ? colors.errorText2 : colors.infoText2
  });

  const descargarExcel = () => {
    const dataParaExcel = empresasZonas.map(relacion => ({
      ID: relacion.id,
      Empresa: relacion.nombreEmpresa || `ID: ${relacion.idEmp}`,
      Zona: relacion.nombreZona || `ID: ${relacion.idZona}`,
    }));

    const csv = [Object.keys(dataParaExcel[0])].concat(dataParaExcel.map(item => Object.values(item))).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'empresas-zonas.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={cajaPrincipalStyle}>
      <LoadingOverlay visible={procesando} />
      <TituloAdmin
        titulo="Administración de Empresas"
        subtitulo="Gestión y visualización de la asignación de empresas a zonas"
      />

      {/* Div central */}
        <div style={cajaContenedorAdminStyle}>

          {/* Mensajes de estado */}
          {error && (
            <div style={alertStyle('error')}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Formulario para crear nueva relación - SIN recuadro grisáceo */}
          <div style={{ marginBottom: '0vh' }}>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '2vh',
              marginBottom: '2.5vh'
            }}>
              <div>
                <Label>
                  Seleccionar Empresa:
                </Label>
                <select
                  ref={empresaSelectRef}
                  value={empresaSeleccionada}
                  onChange={(e) => setEmpresaSeleccionada(e.target.value)}
                  style={selectStyle}
                  disabled={cargando || procesando}
                >
                  <option value="">-- Seleccione una empresa --</option>
                  {empresas.map((empresa) => (
                    <option key={empresa.id} value={empresa.id}>
                      {empresa.nombre_empresa} {empresa?.num > 0 ? `(${empresa.num})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>
                  Seleccionar Zona:
                </Label>
                <select
                  ref={zonaSelectRef}
                  value={zonaSeleccionada}
                  onChange={(e) => setZonaSeleccionada(e.target.value)}
                  style={selectStyle}
                  disabled={cargando || procesando}
                >
                  <option value="">-- Seleccione una zona --</option>
                  {zonas.map((zona) => (
                    <option key={zona.id} value={zona.id}>
                      {zona.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <Boton
                label={procesando ? "Creando..." : "Guardar"}
                onClick={crearRelacion}
                disabled={cargando || procesando || !empresaSeleccionada || !zonaSeleccionada}
                ancho="25vh"
                alto="5vh"
              />
            </div>
          </div>

          {/* Lista de relaciones existentes */}
          <div style={{ marginBottom: '3vh', marginTop: '3vh' }}>

            {cargando ? (
              <LabelCarga nombre="relaciones" />
            ) : empresasZonas.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '4vh',
                color: colors.textSecondary,
                fontSize: '1.6vh',
                fontFamily: 'Inter'
              }}>
                <div style={{ marginBottom: '1vh' }}>📋</div>
                No hay relaciones empresa-zona registradas.
              </div>
            ) : (
              <div className="modern-table-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Empresa</th>
                      <th>Zona</th>
                    </tr>
                  </thead>
                  <tbody>
                    {empresasZonas.map((relacion, index) => (
                      <tr key={relacion.id}>
                        <td>
                          <strong>#{relacion.id}</strong>
                        </td>
                        <td>
                          <div style={{ fontWeight: '500' }}>
                            {relacion.nombreEmpresa || `ID: ${relacion.idEmp}`}
                          </div>
                        </td>
                        <td>
                          <div style={{ fontWeight: '500' }}>
                            {relacion.nombreZona || `ID: ${relacion.idZona}`}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ textAlign: 'right', marginTop: '1.5vh' }}>
            <Boton
              label={"Descargar Excel"}
              onClick={descargarExcel}
              disabled={cargando || !Array.isArray(empresasZonas) || empresasZonas.length === 0}
              color={colors.primary}
              ancho="25vh"
              alto="5vh"
            />
          </div>          

        </div>
        {/* Fin contenido del primer div */}
      </div>
  );
}

export default EmpresasZonas;